import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Copy,
  Check,
  Sparkles,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  FileText,
  Globe,
  Search,
  Zap,
} from 'lucide-react';
import { Message, Source, ChatMode } from '../types';
import { useThemeStore } from '../store/themeStore';

interface ChatMessageProps {
  message: Message;
  isLast?: boolean;
}

const MODE_LABELS: Record<ChatMode, { label: string; icon: React.ReactNode; color: string }> = {
  normal: { label: 'Normal', icon: <Sparkles size={12} />, color: 'text-emerald-400' },
  research: { label: 'Research', icon: <Search size={12} />, color: 'text-blue-400' },
  deep: { label: 'Deep', icon: <Globe size={12} />, color: 'text-purple-400' },
};

const ChatMessage: React.FC<ChatMessageProps> = memo(({ message, isLast }) => {
  const theme = useThemeStore((s) => s.theme);
  const isUser = message.role === 'user';

  // Determine if this normal-mode response was auto-enriched with sources
  const isAutoSearched =
    !isUser &&
    message.mode === 'normal' &&
    message.sources &&
    message.sources.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="w-full py-4"
    >
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className={`flex gap-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
          {/* Assistant Avatar - Sử dụng logo.png */}
          {!isUser && (
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
              <img 
                src="/logo.png" 
                alt="VNGPT" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback nếu logo không load được
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = `<div class="w-full h-full flex items-center justify-center ${
                    theme === 'dark' ? 'bg-gpt-dark-border/50' : 'bg-gpt-light-border'
                  }"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg></div>`;
                }}
              />
            </div>
          )}

          {/* Message Content */}
          <div
            className={`${
              isUser
                ? `max-w-[85%] md:max-w-[70%] rounded-2xl px-5 py-3 ${
                    theme === 'dark' ? 'bg-gpt-dark-message-user' : 'bg-gpt-light-message-user'
                  }`
                : 'flex-1 min-w-0'
            }`}
          >
            {isUser ? (
              <div>
                {/* User attachments */}
                {message.attachments && message.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {message.attachments.map((att, i) => (
                      <div key={i}>
                        {att.previewUrl ? (
                          <img
                            src={att.previewUrl}
                            alt={att.name}
                            className="max-w-[200px] max-h-[150px] rounded-lg object-cover"
                          />
                        ) : (
                          <div
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                              theme === 'dark' ? 'bg-gpt-dark-hover' : 'bg-gpt-light-hover'
                            }`}
                          >
                            <FileText size={14} />
                            <span className="truncate max-w-[150px]">{att.name}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Mode badge for user — show for non-normal */}
                {message.mode && message.mode !== 'normal' && (
                  <div className="flex items-center gap-1 mb-1.5">
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                        MODE_LABELS[message.mode].color
                      } ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'}`}
                    >
                      {MODE_LABELS[message.mode].icon}
                      {MODE_LABELS[message.mode].label}
                    </span>
                  </div>
                )}

                <p
                  className={`text-[15px] leading-7 whitespace-pre-wrap ${
                    theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
                  }`}
                >
                  {message.content}
                </p>
              </div>
            ) : (
              <div>
                {/* Mode indicator for assistant */}
                {message.mode && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-1.5 mb-2 flex-wrap"
                  >
                    {/* Standard mode badge (research/deep) */}
                    {message.mode !== 'normal' && (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          MODE_LABELS[message.mode].color
                        } ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'}`}
                      >
                        {MODE_LABELS[message.mode].icon}
                        {MODE_LABELS[message.mode].label} mode
                      </span>
                    )}

                    {/* Auto-search badge — shown when normal mode used web search */}
                    {isAutoSearched && !message.isStreaming && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium text-amber-400 ${
                          theme === 'dark' ? 'bg-amber-500/10' : 'bg-amber-50'
                        }`}
                      >
                        <Zap size={10} />
                        Auto-searched web
                      </motion.span>
                    )}
                  </motion.div>
                )}

                <div
                  className={`prose max-w-none text-[15px] leading-7 ${
                    theme === 'dark' ? 'prose-invert' : ''
                  }`}
                >
                  {message.isStreaming && message.content === '' ? (
                    <TypingIndicator theme={theme} mode={message.mode} />
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || '');
                          const isInline = !match && !className;

                          if (isInline) {
                            return (
                              <code
                                className={`px-1.5 py-0.5 rounded text-sm font-mono ${
                                  theme === 'dark'
                                    ? 'bg-gpt-dark-input text-pink-400'
                                    : 'bg-gray-100 text-pink-600'
                                }`}
                                {...props}
                              >
                                {children}
                              </code>
                            );
                          }

                          return (
                            <CodeBlock language={match?.[1] || ''} theme={theme}>
                              {String(children).replace(/\n$/, '')}
                            </CodeBlock>
                          );
                        },
                        p({ children }) {
                          return <p className="mb-4 last:mb-0">{children}</p>;
                        },
                        ul({ children }) {
                          return (
                            <ul className="mb-4 space-y-1 list-disc list-inside">{children}</ul>
                          );
                        },
                        ol({ children }) {
                          return (
                            <ol className="mb-4 space-y-1 list-decimal list-inside">{children}</ol>
                          );
                        },
                        li({ children }) {
                          return <li className="leading-7">{children}</li>;
                        },
                        h1({ children }) {
                          return <h1 className="text-2xl font-bold mt-6 mb-4">{children}</h1>;
                        },
                        h2({ children }) {
                          return <h2 className="text-xl font-bold mt-5 mb-3">{children}</h2>;
                        },
                        h3({ children }) {
                          return <h3 className="text-lg font-bold mt-4 mb-2">{children}</h3>;
                        },
                        blockquote({ children }) {
                          return (
                            <blockquote
                              className={`border-l-4 pl-4 italic my-4 ${
                                theme === 'dark'
                                  ? 'border-gpt-dark-border text-gpt-dark-text-secondary'
                                  : 'border-gpt-light-border text-gpt-light-text-secondary'
                              }`}
                            >
                              {children}
                            </blockquote>
                          );
                        },
                        table({ children }) {
                          return (
                            <div className="overflow-x-auto my-4">
                              <table className="w-full">{children}</table>
                            </div>
                          );
                        },
                        a({ children, href }) {
                          return (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gpt-green hover:underline"
                            >
                              {children}
                            </a>
                          );
                        },
                        strong({ children }) {
                          return <strong className="font-semibold">{children}</strong>;
                        },
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  )}

                  {/* Streaming cursor */}
                  {message.isStreaming && message.content !== '' && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity, repeatType: 'reverse' }}
                      className={`inline-block w-2 h-5 ml-0.5 align-middle ${
                        theme === 'dark' ? 'bg-gpt-dark-text' : 'bg-gpt-light-text'
                      }`}
                    />
                  )}
                </div>

                {/* Sources — shown for ALL modes when sources exist */}
                {!message.isStreaming && message.sources && message.sources.length > 0 && (
                  <SourcesSection
                    sources={message.sources}
                    theme={theme}
                    isAutoSearched={isAutoSearched || false}
                    mode={message.mode}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
});

ChatMessage.displayName = 'ChatMessage';

// ─── Sources Section ───
interface SourcesSectionProps {
  sources: Source[];
  theme: string;
  isAutoSearched: boolean;
  mode?: ChatMode;
}

const SourcesSection: React.FC<SourcesSectionProps> = ({
  sources,
  theme,
  isAutoSearched,
  mode,
}) => {
  // Auto-expand for research/deep; collapsed for auto-searched normal
  const [expanded, setExpanded] = useState(mode === 'research' || mode === 'deep');

  const headerColor = isAutoSearched
    ? 'text-amber-400'
    : mode === 'deep'
    ? 'text-purple-400'
    : 'text-blue-400';

  const headerIcon = isAutoSearched ? (
    <Zap size={14} className={headerColor} />
  ) : (
    <Globe size={14} className={headerColor} />
  );

  const headerLabel = isAutoSearched
    ? `${sources.length} web source${sources.length !== 1 ? 's' : ''}`
    : `${sources.length} source${sources.length !== 1 ? 's' : ''}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={`mt-4 rounded-xl border ${
        theme === 'dark'
          ? 'border-gpt-dark-border/50 bg-gpt-dark-input/50'
          : 'border-gpt-light-border bg-gpt-light-input/50'
      }`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors rounded-xl ${
          theme === 'dark'
            ? 'text-gpt-dark-text hover:bg-gpt-dark-hover/50'
            : 'text-gpt-light-text hover:bg-gpt-light-hover/50'
        }`}
      >
        <div className="flex items-center gap-2">
          {headerIcon}
          <span>{headerLabel}</span>
        </div>
        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={16} />
        </motion.div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 space-y-2">
              {sources.map((source, i) => (
                <motion.a
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block p-3 rounded-lg transition-colors group ${
                    theme === 'dark' ? 'hover:bg-gpt-dark-hover' : 'hover:bg-gpt-light-hover'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                        isAutoSearched
                          ? theme === 'dark'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-amber-100 text-amber-600'
                          : theme === 'dark'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-blue-100 text-blue-600'
                      }`}
                    >
                      {source.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm font-medium truncate group-hover:text-gpt-green transition-colors ${
                          theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
                        }`}
                      >
                        {source.title}
                        <ExternalLink size={12} className="inline ml-1 opacity-50" />
                      </p>
                      <p
                        className={`text-[11px] mt-0.5 truncate ${
                          theme === 'dark' ? 'text-gpt-green/60' : 'text-green-600/60'
                        }`}
                      >
                        {extractDomain(source.url)}
                      </p>
                      <p
                        className={`text-xs mt-1 line-clamp-2 ${
                          theme === 'dark'
                            ? 'text-gpt-dark-text-secondary'
                            : 'text-gpt-light-text-secondary'
                        }`}
                      >
                        {source.snippet}
                      </p>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Extract domain from URL for display
const extractDomain = (url: string): string => {
  try {
    const u = new URL(url);
    return u.hostname.replace('www.', '');
  } catch {
    return url;
  }
};

// ─── Code Block ───
interface CodeBlockProps {
  language: string;
  theme: string;
  children: string;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ language, theme, children }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div
      className={`rounded-xl overflow-hidden my-4 ${
        theme === 'dark' ? 'bg-[#1e1e1e]' : 'bg-gray-900'
      }`}
    >
      <div className="flex items-center justify-between px-4 py-2 bg-black/20">
        <span className="text-xs text-gray-400 font-mono">{language || 'plaintext'}</span>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors"
        >
          {copied ? (
            <>
              <Check size={14} />
              Copied!
            </>
          ) : (
            <>
              <Copy size={14} />
              Copy code
            </>
          )}
        </motion.button>
      </div>
      <pre className="overflow-x-auto p-4 !my-0 !bg-transparent">
        <code className="text-sm leading-relaxed text-gray-200 font-mono">{children}</code>
      </pre>
    </div>
  );
};

// ─── Enhanced Typing Indicator với Animation ───
const TypingIndicator: React.FC<{ theme: string; mode?: ChatMode }> = ({ theme, mode }) => {
  const modeConfig = {
    deep: { 
      label: 'Conducting deep research', 
      color: 'text-purple-400',
      bgColor: theme === 'dark' ? 'bg-purple-500/10' : 'bg-purple-50',
      dotColor: 'bg-purple-400'
    },
    research: { 
      label: 'Searching the web', 
      color: 'text-blue-400',
      bgColor: theme === 'dark' ? 'bg-blue-500/10' : 'bg-blue-50',
      dotColor: 'bg-blue-400'
    },
    normal: { 
      label: 'Analyzing your question', 
      color: 'text-emerald-400',
      bgColor: theme === 'dark' ? 'bg-emerald-500/10' : 'bg-emerald-50',
      dotColor: 'bg-emerald-400'
    },
  };

  const config = modeConfig[mode || 'normal'];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-2xl ${config.bgColor}`}
    >
      {/* Animated Dots Container */}
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            className={`w-2 h-2 rounded-full ${config.dotColor}`}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 1, 0.5],
              y: [0, -4, 0],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: index * 0.15,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Label with fade animation */}
      <motion.span
        className={`text-sm font-medium ${config.color}`}
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {config.label}
      </motion.span>

      {/* Optional: Spinning icon for deep/research mode */}
      {(mode === 'deep' || mode === 'research') && (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
          className={config.color}
        >
          {mode === 'deep' ? <Globe size={14} /> : <Search size={14} />}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ChatMessage;